import "antd/dist/reset.css";
import React, { useState } from "react";
import { Form, Input, notification } from "antd";
import { Mail, Lock, Key, ArrowLeft, AlertCircle } from "lucide-react";
import { CSSTransition } from "react-transition-group";
import { useNavigate } from "react-router-dom";
import api from "../../API/api";

const RecuperarSenhaPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasCode, setHasCode] = useState(false);
  const [error, setError] = useState("");

  const handleEmailSubmit = async (values) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post(
        "v1/users/password-reset/",
        {
          email: values.email,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        notification.success({
          message: "Sucesso",
          description: "Um email com instruções foi enviado para sua caixa de entrada.",
          placement: "topRight",
          duration: 4,
        });
        form.resetFields();
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data?.detail || "Email não encontrado.");
        notification.error({
          message: "Erro",
          description: error.response.data?.detail || "Email não encontrado.",
          placement: "topRight",
          duration: 3,
        });
      } else {
        setError("Algo correu mal. Tente novamente.");
        notification.error({
          message: "Erro",
          description: "Algo correu mal. Tente novamente.",
          placement: "topRight",
          duration: 3,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (values) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post(
        "v1/users/password-reset-confirm/",
        {
          code: values.code,
          new_password: values.new_password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        notification.success({
          message: "Sucesso",
          description: "Sua senha foi alterada com sucesso!",
          placement: "topRight",
          duration: 3,
        });
        form.resetFields();
        setHasCode(false);
        setTimeout(() => navigate("/account/login"), 2000);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setError(error.response.data?.detail || "Código inválido ou senha inválida.");
        notification.error({
          message: "Erro",
          description: error.response.data?.detail || "Código inválido ou senha inválida.",
          placement: "topRight",
          duration: 3,
        });
      } else {
        setError("Algo correu mal. Tente novamente.");
        notification.error({
          message: "Erro",
          description: "Algo correu mal. Tente novamente.",
          placement: "topRight",
          duration: 3,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <CSSTransition in={true} appear={true} timeout={300} classNames="fade">
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card de recuperação */}
          <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
            
            {/* Botão Voltar dentro do card */}
            <button
              onClick={() => navigate("/account/login")}
              className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-600 transition-colors text-sm group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Voltar para o login
            </button>

            {/* Logo e título */}
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <img 
                  src={require("../../assets/logo/logo-v2.png")} 
                  alt="UEM Logo" 
                  className="w-20 h-20 object-contain"
                />
                <div className="w-px h-24 bg-gray-300 mx-5"></div>
              </div>
              <div>
                <span className="text-3xl font-bold text-emerald-600 tracking-wide">Alumni</span>
                <p className="text-xs text-gray-500 mt-1">PORTAL DO GRADUADO</p>
              </div>
            </div>

            {/* Título da seção */}
            <div className="text-center">
              <h2 className="text-xl font-light text-gray-800">
                {hasCode ? "Recuperar Senha" : "Esqueceu a senha?"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {hasCode 
                  ? "Introduza o código e a nova senha" 
                  : "Introduza o seu email para receber o código de recuperação"}
              </p>
            </div>

            {/* Mensagem de erro */}
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Formulário */}
            <Form
              form={form}
              name="recover-password"
              onFinish={hasCode ? handleCodeSubmit : handleEmailSubmit}
              className="space-y-5"
            >
              {!hasCode ? (
                <>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Email institucional
                    </label>
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: "Introduza o seu email institucional!" },
                        { type: "email", message: "Introduza um email válido!" },
                      ]}
                      className="mb-0"
                    >
                      <Input
                        prefix={<Mail size={18} className="text-gray-400" />}
                        placeholder="seu.email@uem.ac.mz"
                        className="rounded-xl border-gray-200 py-2.5 px-3 focus:border-emerald-500 focus:ring-emerald-500"
                        size="large"
                      />
                    </Form.Item>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      Caso já possua o código de recuperação,{" "}
                      <button
                        type="button"
                        onClick={() => {
                          form.resetFields();
                          setError("");
                          setHasCode(true);
                        }}
                        className="text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        clique aqui
                      </button>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Código de recuperação
                    </label>
                    <Form.Item
                      name="code"
                      rules={[{ required: true, message: "Introduza o código de recuperação!" }]}
                      className="mb-0"
                    >
                      <Input
                        prefix={<Key size={18} className="text-gray-400" />}
                        placeholder="Código recebido por email"
                        className="rounded-xl border-gray-200 py-2.5 px-3 focus:border-emerald-500 focus:ring-emerald-500"
                        size="large"
                      />
                    </Form.Item>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Nova senha
                    </label>
                    <Form.Item
                      name="new_password"
                      rules={[
                        { required: true, message: "Introduza a nova senha!" },
                        { min: 8, message: "A senha deve ter no mínimo 8 caracteres!" },
                      ]}
                      className="mb-0"
                    >
                      <Input.Password
                        prefix={<Lock size={18} className="text-gray-400" />}
                        placeholder="••••••••"
                        className="rounded-xl border-gray-200 py-2.5 px-3 focus:border-emerald-500 focus:ring-emerald-500"
                        size="large"
                      />
                    </Form.Item>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      Não tem o código?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          form.resetFields();
                          setError("");
                          setHasCode(false);
                        }}
                        className="text-emerald-600 hover:text-emerald-700 font-medium"
                      >
                        solicitar novo código
                      </button>
                    </p>
                  </div>
                </>
              )}

              {/* Botão de ação */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-800 hover:to-emerald-700 text-white font-medium rounded-xl shadow-md shadow-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/30 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>A processar...</span>
                  </div>
                ) : (
                  hasCode ? "RECUPERAR SENHA" : "ENVIAR CÓDIGO"
                )}
              </button>
            </Form>

           
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

export default RecuperarSenhaPage;